import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import api, { getTasks as getTasksApi, createTask, deleteTask, updateExperience, updateLvl } from '../../services/api';
import getLvlFromXP from '../../utils/LevelCalculator';

const useTaskManagement = (userId, selectedPokemon) => {
    const queryClient = useQueryClient();
    const jwtToken = localStorage.getItem('jwtToken');
    

    console.log('useTaskManagement - userId:', userId);
    console.log('Token présent:', !!jwtToken);

    // Query pour récupérer les tâches
    const {
        data: tasks = [],
        isLoading,
        error
    } = useQuery({
        queryKey: ['tasks', userId],
        queryFn: async () => {
            if (!jwtToken) {
                throw new Error('Non authentifié');
            }
            
            try {
                const response = await getTasksApi({ utilisateur: userId });
                return response.data.member || [];
            } catch (error) {
                console.error('Erreur détaillée:', error);
                if (error.response?.status === 401) {
                    throw new Error('Session expirée, veuillez vous reconnecter');
                }
                throw error;
            }
        },
        enabled: !!userId && !!jwtToken,
        staleTime: 1000 * 60 * 5,
        retry: 1,
    });

    console.log('Tasks dans le hook:', tasks); // Log pour vérifier les tâches

    // Mutation pour ajouter une tâche
    const addTaskMutation = useMutation({
        mutationFn: async (taskData) => {
            console.log('Mutation - données envoyées:', taskData);
            const response = await createTask(taskData);
            console.log('Mutation - réponse reçue:', response);
            return response;
        },
        onMutate: async (newTask) => {
            // Annuler les requêtes en cours
            await queryClient.cancelQueries(['tasks', userId]);

            // Snapshot de l'ancien état
            const previousTasks = queryClient.getQueryData(['tasks', userId]);

            // Optimistically update
            queryClient.setQueryData(['tasks', userId], old => {
                const oldTasks = old || [];
                return [...oldTasks, { ...newTask, id: Date.now() }];
            });

            return { previousTasks };
        },
        onError: (err, newTask, context) => {
            console.error('Erreur mutation:', err);
            // Revenir à l'ancien état en cas d'erreur
            queryClient.setQueryData(['tasks', userId], context.previousTasks);
        },
        onSuccess: (newTask) => {
            console.log('Mutation réussie - nouvelle tâche:', newTask);
            // Invalider et refetch
            queryClient.invalidateQueries(['tasks', userId]);
        },
        onSettled: () => {
            // Refetch dans tous les cas
            queryClient.invalidateQueries(['tasks', userId]);
        }
    });

    // Mutation pour supprimer une tâche
    const removeTaskMutation = useMutation({
        mutationFn: async ({ taskId, difficulty, taskPokemon }) => {
            console.log('Suppression tâche:', { taskId, difficulty, taskPokemon });
            
            try {
                await deleteTask(taskId);

                if (taskPokemon) {
                    let experienceGain;
                    switch (difficulty) {
                        case 'easy':
                            experienceGain = 10;
                            break;
                        case 'medium':
                            experienceGain = 20;
                            break;
                        case 'hard':
                            experienceGain = 30;
                            break;
                        default:
                            experienceGain = 10;
                    }

                    const newExperience = taskPokemon.experience + experienceGain;
                    console.log('Mise à jour exp:', { 
                        pokemonId: taskPokemon.user_pokemon_id, 
                        currentExp: taskPokemon.experience,
                        gain: experienceGain,
                        newExperience 
                    });
                    
                    // Mettre à jour l'expérience
                    await updateExperience(taskPokemon.user_pokemon_id, newExperience);

                    // Calculer le nouveau niveau avec getLvlFromXP
                    const currentLevel = taskPokemon.lvl;
                    const newLevel = getLvlFromXP(newExperience, currentLevel);
                    
                    console.log('Calcul niveau:', {
                        currentLevel,
                        newLevel,
                        experience: newExperience
                    });

                    // Si le niveau a changé, mettre à jour
                    if (newLevel > currentLevel) {
                        console.log('Montée de niveau:', {
                            pokemonId: taskPokemon.user_pokemon_id,
                            fromLevel: currentLevel,
                            toLevel: newLevel
                        });
                        await updateLvl(taskPokemon.user_pokemon_id, newLevel);
                    }
                }

                return { taskId, taskPokemon };
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                throw error;
            }
        },
        onSuccess: ({ taskId }) => {
            queryClient.setQueryData(['tasks', userId], (oldTasks = []) => 
                oldTasks.filter(task => task.id !== taskId)
            );
            queryClient.invalidateQueries(['userPokemons']);
        },
        onError: (error) => {
            console.error('Erreur lors de la suppression de la tâche:', error);
        }
    });

    return {
        tasks,
        isLoading,
        error: error?.message,
        addTask: addTaskMutation.mutate,
        removeTask: removeTaskMutation.mutate,
        isAddingTask: addTaskMutation.isLoading,
        isRemovingTask: removeTaskMutation.isLoading
    };
};

export default useTaskManagement; 